import React, { useEffect, useRef, useState } from "react";
import {
  ListLinks,
  WidthContainer,
  Grid,
  TextInput,
  TextMarquee,
  SVG
} from "~components";
import regex from "~constants/regex";
import { useIsClient, useAppContext } from "~hooks";
import cn from "classnames";
import * as styles from "./styles.module.scss";

/** ============================================================================
 * @component
 * Global footer component
 *
 * @return {node}
 */

const Footer = ({ settings }) => {
  const [inputValue, setInputValue] = useState(``);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [hasAPIError, setHasAPIError] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const honeypotRef = useRef(null);

  const isClient = useIsClient();

  const handleChange = (value) => {
    if (hasSubmitted) return;
    setInputValue(value);
    setHasValidationError(false);
    setHasAPIError(false);
  };

  const subscribeUserToNewsletter = async () => {
    const NEWSLETTER_LIST_ID = `YqZLti`;
    const PUBLIC_API_KEY = `W8d6Q7`;
    const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${PUBLIC_API_KEY}`;

    const userEmail = inputValue;

    const body = JSON.stringify({
      data: {
        type: `subscription`,
        attributes: {
          list_id: NEWSLETTER_LIST_ID,
          custom_source: `Website Footer Form`,
          email: userEmail
        }
      }
    });

    const config = {
      method: `POST`,
      headers: {
        revision: `2023-06-15`,
        "content-type": `application/json`
      }
    };

    setIsSubmitting(true);

    try {
      const response = await fetch(url, {
        method: `POST`,
        headers: config.headers,
        body
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok`);
      }

      setHasSubmitted(true);
    } catch (err) {
      console.error(err);
      setHasAPIError(true);
      setInputValue(``);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (hasSubmitted || isSubmitting) return;
    if (honeypotRef.current.value) return;

    if (!regex.email.test(inputValue)) {
      setHasValidationError(true);
    } else {
      subscribeUserToNewsletter();
    }
  };

  const { pathname } = useAppContext();

  const resetForm = () => {
    setInputValue(``);
    setHasValidationError(false);
    setHasAPIError(false);
    setHasSubmitted(false);
    setIsSubmitting(false);
  };

  useEffect(resetForm, [pathname]);

  const {
    newsletterSignup,
    textMarquee,
    linksColumn1,
    linksColumn2,
    linksColumn3
  } = settings || {};

  return (
    <>
      <footer className={cn(styles.footer, { [styles.visible]: isClient })}>
        <WidthContainer paddingOnly>
          <Grid className={styles.content}>
            {newsletterSignup && (
              <form className={styles.newsletter}>
                <label className="b1">
                  <span className={styles.newsletter__CTA}>
                    {newsletterSignup.label}
                  </span>
                  <div
                    className={cn(styles.inputContainer, {
                      [styles.submitted]: hasSubmitted
                    })}
                  >
                    <TextInput
                      placeholder={newsletterSignup.placeholder}
                      onClick={handleSubmit}
                      isDarkTheme
                      className={styles.input}
                      onChange={handleChange}
                      value={inputValue}
                      onEnter={handleSubmit}
                      hasError={hasValidationError}
                      loading={isSubmitting}
                    />
                    <input className={styles.abc} ref={honeypotRef} />
                    <p className={cn(`b2`, styles.successMessage)}>
                      {newsletterSignup.successMessage}
                    </p>
                  </div>
                </label>
                {hasAPIError && (
                  <span className="b2">
                    Something went wrong, please try again later.
                  </span>
                )}
              </form>
            )}
            <nav className={styles.nav}>
              <ul className={cn(`b1`, styles.nav__ul)}>
                <ListLinks links={linksColumn1} />
              </ul>
              <ul className={cn(`b1`, styles.nav__ul)}>
                <ListLinks links={linksColumn2} />
              </ul>
              <ul className={cn(`b1`, styles.nav__ul)}>
                <ListLinks links={linksColumn3} />
              </ul>
            </nav>
          </Grid>
          <div className={cn(styles.copyright, `caption`)}>
            © Loungeface{` `}
            {new Date().getFullYear()}
          </div>
          <SVG svg="wordmark" className={styles.wordmark} />
        </WidthContainer>
      </footer>
      {textMarquee && (
        <TextMarquee
          phrases={textMarquee.text}
          initialBackgroundColor={textMarquee.backgroundColor.hex}
          textColor={textMarquee.textColor.hex}
        />
      )}
    </>
  );
};

export default Footer;
